// Agrupación de pedidos por PRODUCTO PADRE — MÓDULO COMPARTIDO front/back (14-ago-2026).
//
// POR QUÉ EXISTE: esta agrupación vivía inline en `SalesResumen.js` (la pantalla
// Resumen que Isa mira a diario) y la tool `consultar_ventas` del MCP la reimplementaba
// agrupando por `it.name` CRUDO — así una variación por color/talla salía como fila
// propia, dos superficies mostraban un top-5 distinto sobre los mismos datos, y ninguna
// decía cuál estaba mal (el defecto que Miguel vio en la primera consulta real: «Marea»
// dos veces, consumiendo 2 slots y empujando al 5.º producto fuera del corte). Es la
// forma exacta que ya mordió con `expenseClassifier`: un espejo con comentario depende
// de que alguien lo lea; un módulo compartido es estructural. Por eso NO es espejo.
//
// EL PATRÓN (idéntico a formatPct/integridadTexto): ESM con exports NOMBRADOS —lo único
// que webpack verifica estáticamente en el build de Vercel—, importado por el frontend
// normalmente y por el backend vía `require(esm)` (piso Node ≥22.12 para las suites).
// NUNCA volver a CJS-en-src (ver el header de formatPct.js: compila hoy, revienta en el
// próximo build que necesite un helper de babel).
//
// LA CLAVE ES `product_id` (el padre), NO el nombre: todas las variaciones de un producto
// caen en la misma fila. El nombre sale de `parent_name` —que el endpoint `/api/orders`
// INYECTA en cada línea— con fallback a `name`; no se deriva partiendo por « - », porque
// las opciones mismas contienen guiones (medido en scripts/censo-titulo-resumen.js). Las
// dos superficies consumen ese MISMO endpoint (el frontend por browser, la tool por
// loopback), así que alimentadas con el mismo payload devuelven lo mismo por construcción.
//
// FUNCIÓN PURA: no toca red, DOM ni reloj. Devuelve el array agrupado y ordenado por
// cantidad (desempate por ingreso). Cada surface PROYECTA lo que necesita (el frontend
// usa `image` y `variations`; la tool usa `name`/`qty`/`revenue` y `variations.length`).
// La agrupación —lo que NO puede divergir— es una sola; la presentación es de cada lado.

// La etiqueta de variación de UNA línea, desde su meta_data. EXTRAÍDA de groupOrdersByProduct
// —era el bloque inline de acá abajo— para que el backend la comparta SIN reescribirla:
// `fifoService` (el snapshot de costo) y el push POS la importan en vez de filtrar por su
// cuenta, que es como nacieron los ~6 criterios distintos del repo. El criterio es el de
// Resumen/export/inspectores: atributos con `display_key` Y `display_value`, sean LOCALES o
// GLOBALES (`pa_`); nunca las privadas (`_`). Devuelve `null` cuando no hay ninguna (para que
// varKey caiga a '__simple__'); el que necesite string usa `|| ''`. MOVER, no reescribir: el
// test de caracterización de groupOrdersByProduct tiene que seguir verde sin tocarse.
function variationLabelFromItem(item) {
  return (item.meta_data || [])
    .filter((m) => m.display_key && !m.key?.startsWith('_') && m.display_value)
    .map((m) => m.display_value)
    .join(' / ') || null;
}

function groupOrdersByProduct(orders) {
  const map = {};
  for (const order of orders || []) {
    const items = order.line_items || [];
    for (const item of items) {
      const pid = Number(item.product_id) || 0;
      const vid = Number(item.variation_id) || 0;
      const varLabel = variationLabelFromItem(item);
      const imgSrc = item.image?.src || (typeof item.image === 'string' ? item.image : null);

      if (!map[pid]) {
        map[pid] = {
          productId: pid,
          name: item.parent_name || item.name || 'Producto',
          image: imgSrc,
          qty: 0,
          revenue: 0,
          varMap: {},
        };
      }
      if (!map[pid].image && imgSrc) map[pid].image = imgSrc;
      map[pid].qty += Number(item.quantity) || 0;
      map[pid].revenue += parseFloat(item.total) || 0;

      const varKey = vid > 0 ? vid : (varLabel || '__simple__');
      if (!map[pid].varMap[varKey]) {
        map[pid].varMap[varKey] = { variationId: vid, varLabel, qty: 0, revenue: 0 };
      }
      map[pid].varMap[varKey].qty += Number(item.quantity) || 0;
      map[pid].varMap[varKey].revenue += parseFloat(item.total) || 0;
    }
  }

  return Object.values(map)
    .map(({ varMap, ...rest }) => ({
      ...rest,
      variations: Object.values(varMap).sort((a, b) => b.qty - a.qty),
    }))
    .sort((a, b) => b.qty - a.qty || b.revenue - a.revenue);
}

export { groupOrdersByProduct, variationLabelFromItem };
